import { uniqueId } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import Col from 'react-bootstrap/esm/Col'
import FloatingLabel from 'react-bootstrap/esm/FloatingLabel'
import InputGroup from 'react-bootstrap/esm/InputGroup'
import Row from 'react-bootstrap/esm/Row'
import Form from 'react-bootstrap/Form'
import Accordion from 'react-bootstrap/Accordion'
import Dropdown from 'react-bootstrap/Dropdown'
import TriggerDescription from '../TriggerDescription'
import { toast } from 'react-toastify'
import { getValidPreviousIntentId } from '../../../../utils/functions/getValidPreviousIntentId'
import { updateWhatsAppIntent } from '../../../../utils/functions/updateWhatsAppIntent'
import useWebhookState from '../../../../utils/hooks/useWebhookState'
import WhatsappWebhookComponent from './WhatsappWebhookComponent'
import {
  initializeTags,
  addTagToSession,
  removeTagFromSession,
  isTagAvailable
} from '../../../../utils/functions/tagUtils'

interface IProps {
  intent: any
  inputJson: any[]
  setInputJson: React.Dispatch<React.SetStateAction<any[]>>
  closeModal: () => void
  data: any
}

const ListButton: React.FC<IProps> = ({ intent, inputJson, setInputJson, closeModal, data }) => {
  const intentIndex = inputJson.findIndex((item) => item.id === intent.id)
  // const [isImageUrlValid, setIsImageUrlValid] = useState<boolean>(false)
  const { isWebhookChecked, handleWebhookCheckboxChange, handleFulfillmentChange } =
    useWebhookState(intent, intentIndex, inputJson, setInputJson, null)
  const btnMenuDropdownRef = useRef<HTMLDivElement>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    initializeTags()
  }, [])

  const handleChange = (e: any) => {
    const name = e.target.name
    const value = e.target.value
    const newInputJson = [...inputJson]

    if (name === 'intentDescription') {
      newInputJson[intentIndex] = {
        ...intent,
        intentDescription: value
      }
      setInputJson(newInputJson)
    }

    if (name === 'tag') {
      const currentTag = intent?.tag || ''

      if (!isTagAvailable(value, currentTag)) {
        toast.error('This tag name is already in use. Please choose another name.')
        e.target.value = currentTag
        return
      }

      // Remove old tag from session if it exists
      if (currentTag && currentTag.trim() !== '') {
        removeTagFromSession(currentTag)
      }

      // Add new tag to session
      if (value && value.trim() !== '') {
        const tagAdded = addTagToSession(value)
        if (tagAdded) {
          newInputJson[intentIndex] = {
            ...intent,
            tag: value.trim()
          }
          setInputJson(newInputJson)
        }
      } else {
        newInputJson[intentIndex] = {
          ...intent,
          tag: value
        }
        setInputJson(newInputJson)
      }
    }

    if (name === 'headerText') {
      newInputJson[intentIndex] = {
        ...intent,
        interactive: {
          ...intent.interactive,
          header: {
            ...intent.interactive?.header,
            text: value
          }
        }
      }
      setInputJson(newInputJson)
    }

    if (name === 'bodyText') {
      newInputJson[intentIndex] = {
        ...intent,
        interactive: {
          ...intent.interactive,
          body: {
            ...intent.interactive?.body,
            text: value
          }
        }
      }
      setInputJson(newInputJson)
    }

    if (name === 'footerText') {
      newInputJson[intentIndex] = {
        ...intent,
        interactive: {
          ...intent.interactive,
          footer: {
            ...intent.interactive?.footer,
            text: value
          }
        }
      }
      setInputJson(newInputJson)
    }

    if (name === 'buttonText') {
      newInputJson[intentIndex] = {
        ...intent,
        interactive: {
          ...intent.interactive,
          action: {
            ...intent.interactive?.action,
            button: value
          }
        }
      }
      setInputJson(newInputJson)
    }

    if (name.startsWith('sectionTitle')) {
      const sectionIndex = parseInt(name.split('-')[1])

      const sections = [...(intent.interactive?.action?.sections || [])]
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        title: value
      }

      newInputJson[intentIndex] = {
        ...intent,
        interactive: {
          ...intent.interactive,
          action: {
            ...intent.interactive?.action,
            sections: sections
          }
        }
      }
      setInputJson(newInputJson)
    }

    if (name.startsWith('rowTitle')) {
      const sectionIndex = parseInt(name.split('-')[1])
      const rowIndex = parseInt(name.split('-')[2])

      const sections = [...(intent.interactive?.action?.sections || [])]
      const rows = [...(sections[sectionIndex]?.rows || [])]

      rows[rowIndex] = {
        ...rows[rowIndex],
        title: value
      }

      sections[sectionIndex] = {
        ...sections[sectionIndex],
        rows: rows
      }

      newInputJson[intentIndex] = {
        ...intent,
        interactive: {
          ...intent.interactive,
          action: {
            ...intent.interactive?.action,
            sections: sections
          }
        }
      }
      setInputJson(newInputJson)
    }

    if (name.startsWith('rowDescription')) {
      const sectionIndex = parseInt(name.split('-')[1])
      const rowIndex = parseInt(name.split('-')[2])

      const sections = [...(intent.interactive?.action?.sections || [])]
      const rows = [...(sections[sectionIndex]?.rows || [])]

      rows[rowIndex] = {
        ...rows[rowIndex],
        description: value
      }

      sections[sectionIndex] = {
        ...sections[sectionIndex],
        rows: rows
      }

      newInputJson[intentIndex] = {
        ...intent,
        interactive: {
          ...intent.interactive,
          action: {
            ...intent.interactive?.action,
            sections: sections
          }
        }
      }
      setInputJson(newInputJson)
    }

    if (name.startsWith('rowTrigger')) {
      const sectionIndex = parseInt(name.split('-')[1])
      const rowIndex = parseInt(name.split('-')[2])

      const sections = [...(intent.interactive?.action?.sections || [])]
      const rows = [...(sections[sectionIndex]?.rows || [])]

      rows[rowIndex] = {
        ...rows[rowIndex],
        trigger: parseInt(value)
      }

      sections[sectionIndex] = {
        ...sections[sectionIndex],
        rows: rows
      }

      newInputJson[intentIndex] = {
        ...intent,
        interactive: {
          ...intent.interactive,
          action: {
            ...intent.interactive?.action,
            sections: sections
          }
        }
      }
      setInputJson(newInputJson)
    }
  }

  const addRow = (sectionIndex: number, rowIndex: number) => {
    const newInputJson = [...inputJson]
    const sections = [...(intent.interactive?.action?.sections || [])]
    const rows = [...(sections[sectionIndex]?.rows || [])]

    const newRow = {
      id: `LIST_SECTION_${sectionIndex + 1}_ROW_${rows.length + 1}_ID`,
      trigger: 1,
      title: `New Item Title`,
      description: `New Item Description`
    }

    rows.splice(rowIndex + 1, 0, newRow)

    sections[sectionIndex] = {
      ...sections[sectionIndex],
      rows: rows
    }

    newInputJson[intentIndex] = {
      ...intent,
      interactive: {
        ...intent.interactive,
        action: {
          ...intent.interactive?.action,
          sections: sections
        }
      }
    }

    setInputJson(newInputJson)
  }

  const deleteRow = (sectionIndex: number, rowIndex: number) => {
    const newInputJson = [...inputJson]
    const sections = [...(intent.interactive?.action?.sections || [])]
    const rows = [...(sections[sectionIndex]?.rows || [])]

    rows.splice(rowIndex, 1)

    sections[sectionIndex] = {
      ...sections[sectionIndex],
      rows: rows
    }

    newInputJson[intentIndex] = {
      ...intent,
      interactive: {
        ...intent.interactive,
        action: {
          ...intent.interactive?.action,
          sections: sections
        }
      }
    }

    setInputJson(newInputJson)
  }

  const addSection = () => {
    const newInputJson = [...inputJson]
    const sections = [...(intent.interactive?.action?.sections || [])]

    // Check if we've reached max section limit
    if (sections.length >= 10) {
      toast.warning('Maximum 10 sections allowed')
      return
    }

    const newSection = {
      title: `List ${sections.length + 1}`,
      rows: [
        {
          id: `LIST_SECTION_${sections.length + 1}_ROW_1_ID`,
          trigger: 1,
          title: `New Item Title`,
          description: `New Item Description`
        },
        {
          id: `LIST_SECTION_${sections.length + 1}_ROW_2_ID`,
          trigger: 1,
          title: `New Item Title`,
          description: `New Item Description`
        }
      ]
    }

    sections.push(newSection)

    newInputJson[intentIndex] = {
      ...intent,
      interactive: {
        ...(intent.interactive || { type: 'list' }),
        action: {
          ...(intent.interactive?.action || { button: 'Menu' }),
          sections: sections
        }
      }
    }

    setInputJson(newInputJson)
  }

  const deleteSection = (sectionIndex: number) => {
    const newInputJson = [...inputJson]
    const sections = [...(intent.interactive?.action?.sections || [])]

    sections.splice(sectionIndex, 1)

    newInputJson[intentIndex] = {
      ...intent,
      interactive: {
        ...intent.interactive,
        action: {
          ...intent.interactive?.action,
          sections: sections
        }
      }
    }

    setInputJson(newInputJson)
  }

  // Initialize the interactive structure if it doesn't exist
  const initializeInteractive = () => {
    const newInputJson = [...inputJson]

    newInputJson[intentIndex] = {
      ...intent,
      message: '',
      messaging_product: 'whatsapp',
      recipient_type: 'individual',
      type: 'interactive',
      interactive: {
        type: 'list',
        header: {
          type: 'text',
          text: 'Showcasing List Intent'
        },
        body: {
          text: 'Intent Body'
        },
        footer: {
          text: 'Intent Footer'
        },
        action: {
          button: 'Menu',
          sections: [
            {
              title: 'List 1',
              rows: [
                {
                  id: 'LIST_SECTION_1_ROW_1_ID',
                  trigger: 1,
                  title: 'Item 1 Title',
                  description: 'Item 1 Description'
                },
                {
                  id: 'LIST_SECTION_1_ROW_2_ID',
                  trigger: 1,
                  title: 'Item 2 Title',
                  description: 'Item 2 Description'
                }
              ]
            }
          ]
        }
      }
    }

    setInputJson(newInputJson)
  }

  const getTotalRowCount = () => {
    let count = 0
    intent.interactive?.action?.sections?.forEach((section: any) => {
      count += section.rows?.length || 0
    })
    return count
  }

  const handleSave = async () => {
    const token = localStorage.getItem('auth-token')
    const whatsAppIDString = data?.whatsAppIDString
    const previousIntentId = getValidPreviousIntentId(
      intentIndex,
      inputJson,
      data?.WhatsAppInput || []
    )
    setIsSaving(true)
    await updateWhatsAppIntent({
      token,
      whatsAppIDString,
      intent,
      intentJson: inputJson[intentIndex],
      previousIntentId,
      onSuccess: closeModal
    })
  }

  let titleContent = 'List Intent'
  if (intent?.start) {
    titleContent = 'Welcome Intent'
  } else if (intent?.end) {
    titleContent = 'End Intent'
  }

  // If the intent doesn't have the interactive structure, show an initialization button
  if (!intent.interactive) {
    return (
      <>
        <h5 className='card-title h4 mb-5'>{titleContent}</h5>
        <div className='d-flex flex-column align-items-center'>
          <p>This intent needs to be configured as a List Intent.</p>
          <button className='btn btn-primary' onClick={initializeInteractive}>
            Initialize List Intent
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      <h5 className='card-title h4 mb-5'>{titleContent}</h5>
      <>
        <InputGroup className='my-3'>
          <InputGroup.Text>Intent Description</InputGroup.Text>
          <Form.Control
            id={`intentDescription-${intent.id}`}
            type='text'
            name='intentDescription'
            defaultValue={intent?.intentDescription}
            onBlur={handleChange}
          />
        </InputGroup>

        <InputGroup className='my-3'>
          <InputGroup.Text>Tag</InputGroup.Text>
          <Form.Control
            id={`name-${intent.id}`}
            type='text'
            name='tag'
            defaultValue={intent?.tag}
            onBlur={handleChange}
          />
        </InputGroup>

        <InputGroup className='my-3'>
          <InputGroup.Text>Header (optional)</InputGroup.Text>
          <Form.Control
            id={`headerText-${intent.id}`}
            name='headerText'
            defaultValue={intent.interactive?.header?.text}
            onBlur={handleChange}
          />
        </InputGroup>

        <p className='mb-0 mt-2'>Text (max. 1024 characters)</p>
        <InputGroup className='mb-3'>
          <InputGroup.Text>Text</InputGroup.Text>
          <Form.Control
            as='textarea'
            rows={2}
            id={`bodyText-${intent.id}`}
            name='bodyText'
            defaultValue={intent.interactive?.body?.text}
            onBlur={handleChange}
          />
        </InputGroup>

        <InputGroup className='my-3'>
          <InputGroup.Text>Footer (optional)</InputGroup.Text>
          <Form.Control
            id={`footerText-${intent.id}`}
            name='footerText'
            defaultValue={intent.interactive?.footer?.text}
            onBlur={handleChange}
          />
        </InputGroup>

        <InputGroup className='my-3'>
          <InputGroup.Text>List header</InputGroup.Text>
          <Form.Control
            id={`buttonText-${intent.id}`}
            name='buttonText'
            defaultValue={intent.interactive?.action?.button}
            onBlur={handleChange}
          />
        </InputGroup>

        <div className='pb-7'>
          <div className='d-flex justify-content-between align-items-center'>
            <h3 className='fs-5 fw-bolder py-2 mt-3 mb-2'>
              Sections & Items ({getTotalRowCount()}/10)
            </h3>
            <button
              className='btn btn-primary'
              onClick={addSection}
              disabled={getTotalRowCount() >= 10}
            >
              Add Section
            </button>
          </div>
          <small className='text-muted mb-3 d-block'>
            Maximum 10 items total across all sections
          </small>

          <Accordion>
            {intent.interactive?.action?.sections?.map((section: any, sectionIndex: number) => (
              <Accordion.Item key={uniqueId()} eventKey={`section-${sectionIndex}`}>
                <Accordion.Header>
                  <h6 className='mt-n2 mb-n2 text-info'>
                    {section.title || `Section ${sectionIndex + 1}`}
                  </h6>
                </Accordion.Header>
                <Accordion.Body>
                  <div className='d-flex justify-content-between align-items-center mb-3'>
                    <InputGroup>
                      <InputGroup.Text>Section Title</InputGroup.Text>
                      <Form.Control
                        id={`sectionTitle-${sectionIndex}`}
                        name={`sectionTitle-${sectionIndex}`}
                        defaultValue={section.title}
                        onBlur={handleChange}
                      />
                    </InputGroup>
                    <button
                      className='btn btn-danger ms-2'
                      onClick={() => deleteSection(sectionIndex)}
                      disabled={intent.interactive?.action?.sections?.length <= 1}
                    >
                      <i className='bi bi-trash'></i>
                    </button>
                  </div>

                  {section.rows?.map((row: any, rowIndex: number) => (
                    <div key={uniqueId()} className='border p-3 mb-3'>
                      <Form
                        className='my-2'
                        id={`row-${intent.id}-${sectionIndex}-${rowIndex}`}
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <Row>
                          <Col xs={12} className='mt-2'>
                            <FloatingLabel controlId='floatingInput' label='Title'>
                              <Form.Control
                                id={`rowTitle-${sectionIndex}-${rowIndex}`}
                                name={`rowTitle-${sectionIndex}-${rowIndex}`}
                                defaultValue={row.title}
                                onBlur={handleChange}
                              />
                            </FloatingLabel>
                          </Col>
                          <Col xs={12} className='mt-2'>
                            <FloatingLabel controlId='floatingInput' label='Description'>
                              <Form.Control
                                id={`rowDescription-${sectionIndex}-${rowIndex}`}
                                name={`rowDescription-${sectionIndex}-${rowIndex}`}
                                defaultValue={row.description}
                                onBlur={handleChange}
                              />
                            </FloatingLabel>
                          </Col>
                          <Col xs={10} className='mt-2'>
                            <FloatingLabel label='Trigger'>
                              <Form.Select
                                id={`rowTrigger-${sectionIndex}-${rowIndex}`}
                                name={`rowTrigger-${sectionIndex}-${rowIndex}`}
                                aria-label='Select Trigger'
                                defaultValue={row.trigger}
                                onChange={handleChange}
                              >
                                <TriggerDescription inputJson={inputJson} />
                              </Form.Select>
                            </FloatingLabel>
                          </Col>
                          <Col xs={2} ref={btnMenuDropdownRef}>
                            <Dropdown drop='down-centered' className='mt-3'>
                              <Dropdown.Toggle size='sm' variant='secondary' id='dropdown-basic' />
                              <Dropdown.Menu className='border border-secondary'>
                                <Dropdown.Item
                                  onClick={() => addRow(sectionIndex, rowIndex)}
                                  disabled={getTotalRowCount() >= 10}
                                >
                                  <i className='bi bi-plus fs-3 p-0 m-0 text-primary' />
                                  <span className='ms-2'>Add Item</span>
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => deleteRow(sectionIndex, rowIndex)}
                                  disabled={section.rows?.length <= 1}
                                >
                                  <i className='bi bi-trash fs-3 p-0 m-0 text-danger' />
                                  <span className='ms-2'>Delete</span>
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  ))}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>

        <div className='d-flex'>
          <label className='me-3 fs-5 fw-bolder mt-3'>
            Webhook
            <div className='form-check form-switch mt-3'>
              <input
                className='form-check-input'
                type='checkbox'
                id='flexSwitchCheckDefault'
                role='switch'
                name='webhookComponents'
                checked={isWebhookChecked}
                onChange={handleWebhookCheckboxChange}
              />
            </div>
          </label>
        </div>
        {/* )} */}

        {isWebhookChecked && (
          <WhatsappWebhookComponent
            inputJson={inputJson}
            intent={intent}
            handleFulfillmentChange={handleFulfillmentChange}
          />
        )}
        <div className='alert alert-warning mt-4' role='alert'>
          <strong>Buttons Restrictions:</strong>
          <ul className='mb-0'>
            <li>
              🔘 <strong>Buttons</strong>:
              <ul>
                <li>
                  Maximum of <strong>3 buttons</strong> per message
                </li>
                <li>
                  Button title: <strong>max 20 characters</strong>
                </li>
                <li>
                  No formatting allowed (e.g., <code>*bold*</code>, <code>_italic_</code>,{' '}
                  <code>~strikethrough~</code>)
                </li>
                <li>Emojis are allowed</li>
              </ul>
            </li>
            <li>
              📝 <strong>Body Text</strong>:
              <ul>
                <li>
                  Maximum of <strong>1024 characters</strong>
                </li>
                <li>Text will be truncated if the limit is exceeded</li>
              </ul>
            </li>
          </ul>
        </div>
        <div className='d-flex ms-auto mt-auto'>
          <button className='btn btn-secondary me-4' onClick={closeModal}>
            <i className='bi bi-eye-fill' /> Preview
          </button>
          <button className='btn btn-primary' onClick={handleSave} disabled={isSaving}>
            <i className='bi bi-save' /> {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </>
    </>
  )
}

export default ListButton
